import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { signOut as apiSignOut, getUser } from '../api/sign-in'

export const useUserStore = defineStore('user', () => {
  const user = ref({
    signedIn: false,
    email: null,
    displayName: null,
    customers: [],
    customerId: null,
    isAdmin: false,
    canViewComponents: false,
    lastSession: null,
    twoFactorAuthSetUp: false,
    twoFactorAuthVerified: false
  })
  const signingOut = ref(false)

  const signedIn = computed(() => user.value.signedIn)
  const customerId = computed(() => user.value.customerId)
  const customers = computed(() => user.value.customers)
  const customerUrlSlug = computed(
    () => (user.value.customers.find(c => c.id === user.value.customerId) || null)?.url_slug
  )
  const isAdmin = computed(() => signedIn.value?.isAdmin)
  const twoFactorAuthSetUp = computed(() => user.value?.twoFactorAuthSetUp ?? false)
  const twoFactorAuthVerified = computed(() => user.value?.twoFactorAuthVerified ?? false)

  function setUser(
    email,
    displayName,
    customers,
    isAdmin,
    canViewComponents,
    lastSession,
    twoFactorAuthSetup,
    twoFactorAuthVerified
  ) {
    user.value.signedIn = true
    user.value.email = email
    user.value.displayName = displayName
    user.value.customers = customers
    user.value.customerId = customers.length === 1 ? customers.find(() => true).id : null
    user.value.isAdmin = isAdmin
    user.value.canViewComponents = canViewComponents
    user.value.lastSession = lastSession
    user.value.twoFactorAuthSetUp = twoFactorAuthSetup
    user.value.twoFactorAuthVerified = twoFactorAuthVerified
  }

  function unsetUser() {
    user.value.signedIn = false
    user.value.email = null
    user.value.displayName = null
    user.value.customers = []
    user.value.customerId = null
    user.value.isAdmin = false
    user.value.canViewComponents = false
    user.value.lastSession = null
    user.value.twoFactorAuthSetUp = false
    user.value.twoFactorAuthVerified = false
  }

  function setCustomerId(id) {
    user.value.customerId = id
  }

  function setCustomerByUrlSlug(urlSlug) {
    const customer = user.value.customers.find(c => c.url_slug === urlSlug) || null
    if (customer) {
      user.value.customerId = customer.id
    } else {
      user.value.customerId = null
    }
  }

  function setTwoFactorAuthVerified(isVerified) {
    user.value.twoFactorAuthVerified = isVerified
  }

  function getCustomerInstallationsUrl(customerId) {
    const customer = user.value.customers.find(c => c.id === customerId) ?? null
    const slug = customer?.url_slug ?? null
    return slug ? `/installations/${slug}` : ''
  }

  async function signOut() {
    signingOut.value = true
    try {
      await apiSignOut()
      unsetUser()
    } finally {
      signingOut.value = false
    }
  }

  async function fetchUser() {
    try {
      const data = await getUser()
      setUser(
        data.email,
        data.display_name,
        data.customers,
        data.is_admin,
        data.can_view_components,
        data.last_session,
        data.two_factor_auth_set_up,
        data.two_factor_auth_verified
      )
    } catch (_e) {
      unsetUser()
    }
  }

  return {
    user,
    signedIn,
    signingOut,
    customerId,
    customers,
    customerUrlSlug,
    isAdmin,
    twoFactorAuthSetUp,
    twoFactorAuthVerified,
    setUser,
    unsetUser,
    setCustomerId,
    setCustomerByUrlSlug,
    signOut,
    fetchUser,
    setTwoFactorAuthVerified,
    getCustomerInstallationsUrl
  }
})
